import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ProjectPickerList } from "@/components/building/ProjectPickerList";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { BuildingScopeProjectOption } from "@/hooks/buildings/useBuildingScope";

interface ProjectPickerPanelProps {
  projects: BuildingScopeProjectOption[];
  /** Currently scoped project, checked in the list. Omit when nothing is selected yet. */
  selectedProjectId?: number | null;
  /** True once the options for this building have actually loaded. */
  optionsLoaded: boolean;
  /** Load error from the last option load, if it failed. */
  optionsError: string | null;
  /** Retry a failed option load — the load effect never retries on its own. */
  onReload: () => void;
  onSelect: (projectId: number) => void;
  /**
   * Start creating a project from the picker. Omit to hide the option — the
   * caller owns the create-projects permission gate and the dialog.
   */
  onCreateProject?: () => void;
  /** Offer "Whole building" above the projects; see ProjectPickerList. */
  onSelectWholeBuilding?: () => void;
  /** Whether "Whole building" is ticked; see ProjectPickerList. */
  isWholeBuildingSelected?: boolean;
}

/**
 * The body of the sidebar view-mode toggle's project dropdown. It owns the
 * three states an option load can be in (loaded → the searchable list;
 * failed → the retry prompt; still in flight → a spinner). The caller owns the popover, the
 * permission gate behind `onCreateProject`, and what a selection does.
 */
export function ProjectPickerPanel({
  projects,
  selectedProjectId = null,
  optionsLoaded,
  optionsError,
  onReload,
  onSelect,
  onCreateProject,
  onSelectWholeBuilding,
  isWholeBuildingSelected,
}: ProjectPickerPanelProps) {
  const { t } = useLanguage();

  if (optionsLoaded) {
    return (
      <ProjectPickerList
        projects={projects}
        selectedProjectId={selectedProjectId}
        onSelect={onSelect}
        onCreateProject={onCreateProject}
        onSelectWholeBuilding={onSelectWholeBuilding}
        isWholeBuildingSelected={isWholeBuildingSelected}
      />
    );
  }

  if (optionsError) {
    return (
      <div className="flex flex-col items-center gap-3 p-4 text-center">
        <p className="text-sm text-muted-foreground">{t.buildings.scope.loadFailed}</p>
        <Button variant="outline" size="sm" onClick={onReload}>
          {t.common.retry}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-24 items-center justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  );
}
